// One imperative write, with its pending flag and its failure copy (plan 101
// doc 15, step 5). The generalisation of the ten controllers that had already
// been extracted by hand — `createMachineRemove`, `tokenRevoke`, `issue-actions`,
// `box-actions` and the rest — every one of which had rewritten the same
// `setBusy(true) / try / catch → errorReason / finally setBusy(false)` ladder.
//
// NO CLIENT VALIDATION, and that is doctrine rather than an omission
// (`apps/dashboard/CLAUDE.md`): the server is the arbiter and its 422 `detail`
// is the sentence the reader is shown. This kit therefore takes a `call` and a
// fallback string; it never inspects an argument before sending it.
//
// THE RE-ENTRANCY GUARD IS THE PART THE HAND-ROLLED SITES KEPT MISSING, and the
// number is counted rather than asserted: across the 19 surfaces migrated onto
// this kit there are 23 write paths, and 14 of them set a `busy` signal to
// DISABLE a control while never consulting it on the way in. The nine that did
// guard are the destructive confirms; every plain form submit but one did not.
// `<OssVerifyPanel/>` is the 19th and its test is the demonstration: two submits
// in one tick fire ONE POST, and removing the `inFlight` line below makes that
// test report two.
// A `disabled` attribute is a rendering, not a lock — Enter in a text field, a
// form's implicit submit and a second click landing in the same tick all reach
// the handler directly. Here the guard is unconditional and cannot be forgotten.

import { createSignal } from 'solid-js';
import { errorReason } from './api-errors';

/** One write's state. `run` never rejects: a failure lands in `error()`. */
export type Mutation<A extends readonly unknown[]> = {
  /**
   * Fire the write. Awaitable (so a test can settle it), and safe to drop on the
   * floor in a handler — it resolves on failure too. A call made while one is in
   * flight is DROPPED, not queued: these are user-initiated writes, and the
   * second click of a double click means the same intent as the first.
   */
  readonly run: (...args: A) => Promise<void>;
  /** True from the moment `run` is entered until its call settles. */
  readonly pending: () => boolean;
  /** Failure copy from the last attempt, or null. `errorReason`, so a 422 `detail` wins. */
  readonly error: () => string | null;
  /** Clear the failure notice — wire to a `<Toast/>`'s `onDismiss`. */
  readonly clearError: () => void;
};

export type MutationInput<A extends readonly unknown[], R> = {
  /** The write itself — usually one `apiFetch`. Rejects on any non-2xx. */
  readonly call: (...args: A) => Promise<R>;
  /**
   * Shown when the failure carries no `detail` of its own. Pass a `t('…')`, never
   * a literal: this string reaches the screen.
   */
  readonly fallback: string;
  /** Ran after the API accepted the write — refetch, close the dialog, notify. */
  readonly onSuccess?: (result: R) => void;
  /**
   * Ran with the same string `error()` takes, for a surface that reports through
   * a parent's `<Toast/>` rather than rendering the failure itself. Both happen:
   * a caller reads whichever it needs, and no caller has to sync one to the other
   * through a `createEffect` — which is the thing this seam exists to prevent.
   *
   * `cause` is the rejection itself, because a string is not always enough to
   * decide what to render: `SshEnrollPanel` reads the STATUS to tell a seat-cap
   * 402 (hard stop) from a transport 503 (retry in a moment), and collapsing that
   * to its sentence would make the two indistinguishable.
   */
  readonly onError?: (message: string, cause: unknown) => void;
  /**
   * Re-read server truth, on BOTH outcomes, AWAITED INSIDE THE PENDING WINDOW.
   *
   * Not a convenience — it is the shape four of the migrated surfaces already
   * hand-rolled (`RepoPendingTab`, `Integrations`, `MyKeys`, `AccountKeys`), each
   * with the resync in its `finally` ABOVE `setBusy(false)`. `OrgMembers` is a
   * fifth instance of the shape and is deliberately NOT migrated: its local
   * `run()` takes a per-call `mapError`, which this kit does not model.
   * The ordering is the point: these screens keep the control disabled until the
   * list has re-read, because releasing it earlier offers a second click over a
   * row the screen has not corrected yet — and a 409 raced decision resolves the
   * same way a success does, which is why it cannot hang off `onSuccess`.
   *
   * Returns `unknown` so a bare `() => query.refetch()` is legal — a TanStack
   * refetch resolves to a result object, and forcing every call site to discard
   * it would buy nothing.
   */
  readonly onSettled?: () => unknown;
};

/**
 * A write for one surface. Per-surface state, never a global store: the caller
 * holds it, and it unit-tests without a shell.
 *
 * A failure leaves `error()` set and does NOT call `onSuccess`, so a caller that
 * closes its dialog there cannot dismiss itself over a write the server refused
 * — a destructive action must never silently no-op.
 */
export function createMutation<A extends readonly unknown[], R>(
  input: MutationInput<A, R>,
): Mutation<A> {
  const [pending, setPending] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // A plain binding, not the `pending` signal, and deliberately: this is the
  // lock, and it is set synchronously before the first `await` so two calls in
  // ONE tick cannot both pass. Reading a signal here would be correct today and
  // fragile the first time a caller wrapped `run` in `batch`.
  let inFlight = false;

  const run = async (...args: A): Promise<void> => {
    if (inFlight) return;
    inFlight = true;
    setPending(true);
    setError(null);
    try {
      const result = await input.call(...args);
      input.onSuccess?.(result);
    } catch (err) {
      const message = errorReason(err, input.fallback);
      setError(message);
      input.onError?.(message, err);
    } finally {
      try {
        await input.onSettled?.();
      } catch {
        // A resync is a TanStack refetch: its failure is already that query's own
        // error state, rendered by the surface's `<QueryBoundary/>`. Reporting it
        // here as well would blame this WRITE for a read that failed after it
        // succeeded — and would reject `run`, which nothing in the app awaits.
      }
      inFlight = false;
      setPending(false);
    }
  };

  return { run, pending, error, clearError: () => setError(null) };
}
